import React, { useState, useMemo, useRef, useCallback } from 'react';
import { VehicleModel, Manufacturer } from '../../types';
import logger from '../../utils/logger';
import { useMasterData } from '../../hooks/useMasterData';
import { useAuth } from '../../hooks/useAuth';
import {
  TagIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Input from '../ui/Input';
import { useModal } from '../../hooks/useModal';

interface VehicleModelSelectorProps {
  value?: string; // selected vehicleModelId
  onChange: (vehicleModelId: string | null) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
}

interface VehicleModelOption {
  vehicleModelId: string;
  displayName: string;
  vehicleModel: VehicleModel;
  manufacturer?: Manufacturer | null;
  searchText: string;
}

export const VehicleModelSelector: React.FC<VehicleModelSelectorProps> = ({
  value,
  onChange,
  label = '車両モデル',
  placeholder = '車両モデルを選択してください',
  required = false,
  disabled = false,
  className = '',
}) => {
  const { user } = useAuth();
  const { getFilteredVehicleModels, getManufacturerById, getVehicleModelById } = useMasterData();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // システム設定でフィルタリングされた車両モデルを取得（基本リスト）
  const baseFilteredVehicleModels = useMemo(() => {
    if (!user?.companyId) {
      if (process.env.NODE_ENV !== 'test') {
        logger.warn('⚠️ VehicleModelSelector: 会社IDが未設定のため、車両モデルを取得できません');
      }
      return [];
    }

    const filtered = getFilteredVehicleModels(user.companyId);

    logger.info('[VehicleModelSelector] フィルタリング結果:', {
      companyId: user.companyId,
      filteredCount: filtered.length,
      filteredIds: filtered.map(vm => vm.id),
    });

    return filtered;
  }, [user?.companyId, getFilteredVehicleModels]);

  // 編集モード対応: 現在選択中の車両モデルIDが基本リストに含まれていない場合は追加
  const filteredVehicleModels = useMemo(() => {
    if (value && !baseFilteredVehicleModels.find(vm => vm.id === value)) {
      const currentModel = getVehicleModelById(value);
      if (currentModel) {
        logger.info('[VehicleModelSelector] 編集モード: 現在選択中の車両モデルを追加:', {
          vehicleModelId: value,
          vehicleModelName: currentModel.name,
          reason: 'システム設定で無効化されているが、既存案件で使用中',
        });
        return [...baseFilteredVehicleModels, currentModel];
      } else {
        logger.warn('[VehicleModelSelector] 編集モード: 車両モデルが見つかりません:', {
          vehicleModelId: value,
          reason: 'DBから削除された可能性があります',
        });
      }
    }

    return baseFilteredVehicleModels;
  }, [baseFilteredVehicleModels, value, getVehicleModelById]);

  // 車両モデルオプションを作成
  const vehicleModelOptions = useMemo((): VehicleModelOption[] => {
    return filteredVehicleModels.map(vehicleModel => {
      const manufacturer = getManufacturerById(vehicleModel.manufacturerId);
      const displayName = manufacturer
        ? `${manufacturer.name} ${vehicleModel.name}`
        : vehicleModel.name;

      const searchText = `${manufacturer?.name || ''} ${vehicleModel.name}`.toLowerCase();

      return {
        vehicleModelId: vehicleModel.id,
        displayName,
        vehicleModel,
        manufacturer,
        searchText,
      };
    });
  }, [filteredVehicleModels, getManufacturerById]);

  // 検索でフィルタリングされたオプション
  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) {
      return vehicleModelOptions;
    }

    const query = searchQuery.toLowerCase();
    return vehicleModelOptions.filter(
      option =>
        option.searchText.includes(query) ||
        option.vehicleModel.name.toLowerCase().includes(query) ||
        option.manufacturer?.name.toLowerCase().includes(query)
    );
  }, [vehicleModelOptions, searchQuery]);

  // 選択された車両モデルの情報
  const selectedOption = useMemo(() => {
    if (!value) {
      logger.info('[VehicleModelSelector] 選択値なし - value:', value);
      return null;
    }

    const found = vehicleModelOptions.find(option => option.vehicleModelId === value);
    logger.info('[VehicleModelSelector] 選択された車両モデル:', {
      inputValue: value,
      foundOption: found,
      availableOptionsCount: vehicleModelOptions.length,
      firstOption: vehicleModelOptions[0],
    });

    if (!found) {
      logger.warn('[VehicleModelSelector] 車両モデルが見つかりません:', {
        searchValue: value,
        availableIds: vehicleModelOptions.map(opt => opt.vehicleModelId),
      });
    }

    return found;
  }, [value, vehicleModelOptions]);

  // モーダル管理
  const { handleOverlayClick } = useModal({
    isOpen,
    onClose: () => setIsOpen(false),
    containerRef,
    initialFocusRef: searchInputRef,
    lockBodyScroll: false,
    closeOnEscape: true,
    closeOnOverlayClick: true,
  });

  const handleOpen = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
      setSearchQuery('');
    }
  }, [disabled]);

  const handleOptionSelect = useCallback(
    (option: VehicleModelOption) => {
      onChange(option.vehicleModelId);
      setIsOpen(false);
      setSearchQuery('');
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    onChange(null);
    setSearchQuery('');
  }, [onChange]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
          {label}
          {required && <span className='text-red-500 ml-1'>*</span>}
        </label>
      )}

      {/* 選択フィールド */}
      <div className='relative' ref={containerRef}>
        <button
          type='button'
          onClick={handleOpen}
          disabled={disabled}
          data-testid='vehicle-model-selector-button'
          className={`w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-left focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-target-input flex items-center justify-between ${
            isOpen ? 'ring-1 ring-primary-500 border-primary-500' : ''
          }`}
        >
          <div className='flex items-center flex-1 min-w-0'>
            <TagIcon className='h-5 w-5 text-gray-400 mr-3 flex-shrink-0' />
            <span
              className={`truncate ${
                selectedOption
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {selectedOption ? selectedOption.displayName : placeholder}
            </span>
          </div>

          <div className='flex items-center ml-2'>
            {selectedOption && (
              <div
                role='button'
                tabIndex={0}
                onClick={e => {
                  e.stopPropagation();
                  handleClear();
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClear();
                  }
                }}
                className='p-1 hover:bg-gray-100 dark:hover:bg-gray-600 rounded mr-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500'
                aria-label='選択をクリア'
              >
                <XMarkIcon className='h-4 w-4 text-gray-400' />
              </div>
            )}
            <ChevronDownIcon
              className={`h-5 w-5 text-gray-400 transition-transform ${
                isOpen ? 'transform rotate-180' : ''
              }`}
            />
          </div>
        </button>

        {/* ドロップダウンメニュー */}
        {isOpen && (
          <>
            {/* オーバーレイ */}
            <div className='fixed inset-0 z-10' onClick={handleOverlayClick} aria-hidden='true' />

            {/* ドロップダウンコンテンツ */}
            <div className='absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-80 overflow-hidden'>
              {/* 検索フィールド */}
              <div className='p-3 border-b border-gray-200 dark:border-gray-700'>
                <div className='relative'>
                  <MagnifyingGlassIcon className='absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400' />
                  <Input
                    ref={searchInputRef}
                    type='text'
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder='車両モデルを検索...'
                    className='pl-10'
                  />
                </div>
              </div>

              {/* オプションリスト */}
              <div className='max-h-60 overflow-y-auto'>
                {filteredOptions.length > 0 ? (
                  filteredOptions.map((option, index) => (
                    <button
                      key={option.vehicleModelId}
                      type='button'
                      onClick={() => handleOptionSelect(option)}
                      data-testid={index === 0 ? 'vehicle-model-option-first' : undefined}
                      className={`w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:bg-gray-50 dark:focus:bg-gray-700 ${
                        option.vehicleModelId === value
                          ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                          : 'text-gray-900 dark:text-white'
                      }`}
                    >
                      <div className='flex items-center'>
                        <TagIcon className='h-5 w-5 text-gray-400 mr-3 flex-shrink-0' />
                        <div className='flex-1 min-w-0'>
                          <div className='text-sm font-medium truncate'>{option.displayName}</div>
                          {option.manufacturer && (
                            <div className='text-xs text-gray-500 dark:text-gray-400 mt-1'>
                              メーカー: {option.manufacturer.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className='px-4 py-6 text-center text-gray-500 dark:text-gray-400'>
                    <TagIcon className='h-8 w-8 mx-auto mb-2 text-gray-300 dark:text-gray-600' />
                    <p className='text-sm'>
                      {searchQuery
                        ? '検索結果が見つかりません'
                        : '利用可能な車両モデルがありません'}
                    </p>
                    {!searchQuery && user?.companyId && (
                      <p className='text-xs mt-1'>システム設定で車両モデルを有効化してください</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
